/**
 * 経費の入力(日別のポップアップ。2026-09-10 指示: 一覧は閲覧だけにして、日付の「入力」から開く)。
 * その日の項目ごとの金額を入れて、その日だけ保存する。マイナス(利益の戻しなど)も入れられる。
 */
import { useEffect, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import Modal from '@core/components/Modal';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import { weekdayLabel } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import type { DateStr } from '@core/lib/types';
import { val } from './deliveryEdit';
import { saveExpenses, type ExpenseItem } from './expenseRepo';

export default function ExpenseDayModal({
  date,
  items,
  initial,
  onClose,
  onSaved,
  onDirtyChange,
}: {
  date: DateStr;
  items: ExpenseItem[];
  /** 項目ID → 金額(文字列) */
  initial: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(items.map((i) => [i.id, initial[i.id] ?? ''])));
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const dirty = items.some((i) => (values[i.id] ?? '') !== (initial[i.id] ?? ''));

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  const dateLabel = `${date.slice(0, 4)}/${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}${weekdayLabel(date)}`;
  const total = items.reduce((s, i) => s + val(values[i.id] ?? ''), 0);

  async function save() {
    if (!user) return;
    setBusy(true);
    try {
      const out: Record<string, number> = {};
      for (const i of items) {
        const s = (values[i.id] ?? '').trim();
        if (s === '') continue;
        const n = val(s);
        if (!Number.isInteger(n)) throw new Error(`「${i.name}」は整数(円)で入力してください`);
        out[i.id] = n;
      }
      await saveExpenses({ items, changed: [{ date, values: out }], userId: user.id });
      toast(`${dateLabel} の経費を保存しました(合計 ¥${formatYen(total)})`);
      onDirtyChange(false);
      onSaved();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const requestClose = () => (dirty ? setConfirmClose(true) : onClose());
  const inputStyle = { width: 120, padding: '3px 8px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', textAlign: 'right', fontSize: 13 } as const;

  return (
    <Modal onClose={requestClose} width="min(560px, 94vw)">
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        経費の入力
        <span className="day-label">{dateLabel}</span>
        <span className="hint" style={{ fontWeight: 400 }}>合計 ¥{formatYen(total)}</span>
      </h3>
      {items.length === 0 ? (
        <p className="hint">項目がありません。一覧の空欄に項目名を入れてから入力してください。</p>
      ) : (
        <table className="fit-table no-stack">
          <thead>
            <tr>
              <th>項目</th>
              <th className="num">金額(円)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id}>
                <td>{i.name}</td>
                <td className="num">
                  <NumInput
                    value={values[i.id] ?? ''}
                    onChange={(v) => setValues((prev) => ({ ...prev, [i.id]: v }))}
                    style={inputStyle}
                    disabled={busy}
                    aria-label={i.name}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, background: 'var(--legacy-shade)' }}>
              <td>合計</td>
              <td className="num">¥{formatYen(total)}</td>
            </tr>
          </tfoot>
        </table>
      )}
      <div className="modal-actions center">
        <button className="btn-green" disabled={busy || !dirty} onClick={() => void save()}>
          {busy ? '保存中…' : `保存する(${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))})`}
        </button>
        <button className="btn-line" onClick={requestClose} disabled={busy}>
          閉じる
        </button>
      </div>
      {confirmClose && (
        <Modal onClose={() => setConfirmClose(false)} width="min(460px, 94vw)" showClose={false}>
          <h3>保存されていない変更があります</h3>
          <p style={{ margin: '8px 0 0' }}>このまま閉じると、{dateLabel} に入力した内容は失われます。</p>
          <div className="modal-actions center">
            <button className="btn-green" onClick={() => setConfirmClose(false)}>
              入力に戻る
            </button>
            <button
              className="btn-line danger"
              onClick={() => {
                setConfirmClose(false);
                onDirtyChange(false);
                onClose();
              }}
            >
              保存せずに閉じる
            </button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
